import axios from "axios";

export type BillingPeriod = "month" | "year";

export type Money = {
  amount: number;
  currency: string;
};

export type PreviewLineItem = {
  bundleId: string;
  itemPriceId?: string;
  amount: number;
  owned: boolean;
  ending: boolean;
};

export type CheckoutPreview = {
  billingPeriod: BillingPeriod;
  currency: string;
  lineItems: PreviewLineItem[];
  payNow: Money;
  nextCharge: Money & { at: string | null; bundles: string[] };
  dailyPrompts: { current: number; afterRenewal: number };
  endingBundles: string[];
  paymentMethod: { brand: string; last4: string } | null;
  source: "chargebee-estimate" | "partial" | "catalogue";
};

export type PurchaseResult = {
  subscription?: unknown;
  charged: Money & { invoiceId?: string };
};

export type BundlePurchaseReceipt = {
  bundleNames: string[];
  charged: Money;
  promptsBefore?: number;
  promptsNow?: number;
  renewal?: { amount: number; currency: string; at: string | null } | null;
  billingPeriod: BillingPeriod;
};

export type PurchaseFailure = {
  status?: number;
  code?: string;
  message: string;
  needsCard: boolean;

  // 402 payment_pending is the exception: the invoice is uncollected but the bundles ARE on the sub.
  subscriptionChanged: boolean;
};

type PurchaseRequestBody = {
  bundles: string[];

  // The SUBSCRIPTION's own period, never the Monthly/Annual toggle — SPL 400s on a mismatch.
  billing_period: BillingPeriod;
  currency?: string;
};

export type PurchaseRequest = {
  bundles: string[];
  billingPeriod: BillingPeriod;
  currency?: string;
};

const splUrl = (path: string) => `${process.env.REACT_APP_SPL_URL}${path}`;

const body = ({ bundles, billingPeriod, currency }: PurchaseRequest): PurchaseRequestBody => ({
  bundles,
  billing_period: billingPeriod,
  ...(currency ? { currency } : {}),
});

export const fetchCheckoutPreview = async (req: PurchaseRequest): Promise<CheckoutPreview> => {
  const { data } = await axios.post(
    splUrl("/v2/subscriptions/checkout-preview"),
    body(req),
    { withCredentials: true },
  );
  return data as CheckoutPreview;
};

export const purchaseBundles = async (req: PurchaseRequest): Promise<PurchaseResult> => {
  const { data } = await axios.post(
    splUrl("/v2/subscriptions/bundles-purchase"),
    body(req),
    { withCredentials: true },
  );
  return data as PurchaseResult;
};

export const openPaymentMethodPage = async (): Promise<string> => {
  const { data } = await axios.post(
    splUrl("/v2/subscriptions/payment-method-page"),
    undefined,
    { withCredentials: true },
  );

  const url = (data?.hosted_page ?? data)?.url;
  if (!url) throw new Error("No payment page URL returned");
  window.location.assign(url);
  return url;
};

const failureMessage = (status: number | undefined, data: unknown): string => {
  if (typeof data === "string" && data.trim()) return data;
  const fromBody = (data as { message?: string })?.message;
  if (fromBody) return fromBody;
  if (status === 402) return "That card was declined. Update your payment method and try again.";
  if (status === 409) return "You don't have a card on file yet. Add one to complete this purchase.";
  return "Something went wrong completing your purchase. Please try again.";
};

export const toPurchaseFailure = (error: unknown): PurchaseFailure => {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const status = response?.status;
  const data = response?.data;
  return {
    status,
    code: (data as { code?: string })?.code,
    message: failureMessage(status, data),
    needsCard: status === 402 || status === 409,
    subscriptionChanged: (data as { subscriptionChanged?: boolean })?.subscriptionChanged === true,
  };
};

const PENDING_KEY = "tgpt_pending_bundle_purchase";

export const rememberPendingPurchase = (bundles: string[]) => {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(bundles));
  }
  catch {
    // sessionStorage is unavailable in private mode
  }
};

export const readPendingPurchase = (): string[] => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter(id => typeof id === "string") : [];
  }
  catch {
    return [];
  }
};

export const clearPendingPurchase = () => {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  }
  catch {
    // sessionStorage is unavailable in private mode
  }
};

export const formatMoney = (amount: number, currency = "USD"): string => {
  const value = amount / 100;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  catch {
    return `$${value.toFixed(2)}`;
  }
};

export const termSuffix = (period: BillingPeriod) => (period === "year" ? "yr" : "mo");
